"use client"

import { useMemo, useState } from "react"
import { DropzoneOptions } from "react-dropzone"
import { Plus, Edit, Trash2, ChevronRight, ChevronDown, Save, X, PlusCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import {
    FileUploader,
    FileUploaderContent,
    FileUploaderItem,
    FileInput,
} from "@/components/ui/file-uploader"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import {
    useGetCategoriesQuery,
    useCreateCategoryMutation,
    useUpdateCategoryMutation,
    useDeleteCategoryMutation,
} from "@/services/categoryApi"
import { useSelector } from "react-redux"
import { RootState } from "@/redux/store"
import { useToast } from "@/components/ui/use-toast"
import { ConfirmationDialog } from "@/components/ui/common/confirmation-dailog"
import { useForm } from "react-hook-form"
import { ErrorType } from "@/types"
import useDocumentTitle from "@/components/ui/common/document-title"
import EmptyCategory from "@/assets/empty_categoery.png"
import uploadimg from "@/assets/upload.svg"
import { deleteCognitoImage, UploadCognitoImage } from "@/lib/utils/s3Service"
const { VITE_VIEW_S3_URL } = import.meta.env

type CategoryFormData = {
    name: string
    parent_category_id: number
    image_url?: string | null
}

export default function CategoriesPage() {
    useDocumentTitle("Categories")
    const { toast } = useToast()
    const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0

    const [activeType, setActiveType] = useState<"facility" | "product" | "member" | "membership" | "credit_add_ons" | "coach">("facility")
    const [isAddingCategory, setIsAddingCategory] = useState(false)
    const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({})
    const [editingCategory, setEditingCategory] = useState<number | null>(null)
    const [addingSubcategoryTo, setAddingSubcategoryTo] = useState<number | null>(null)
    const [files, setFiles] = useState<File[] | null>([])

    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [categoryToDelete, setCategoryToDelete] = useState<number | null>(null)

    const { data: categoryData, isLoading } = useGetCategoriesQuery({
        org_id: orgId,
        query: `type=${activeType}&load_subcategories=true&sort_key=id&sort_order=desc`
    })

    const [createCategory] = useCreateCategoryMutation()
    const [updateCategory] = useUpdateCategoryMutation()
    const [deleteCategory] = useDeleteCategoryMutation()

    const { register, handleSubmit, reset, setValue, watch } = useForm<CategoryFormData>({
        defaultValues: { name: "", parent_category_id: 0, image_url: "" }
    })
    const watcher = watch()

    const categories = useMemo(() => Array.isArray(categoryData?.data) ? categoryData.data : [], [categoryData])
    const dropzone = {
        accept: {
            "image/": [".jpg", ".jpeg", ".png"],
        },
        multiple: true,
        maxFiles: 1,
        maxSize: 1 * 1024 * 1024,
    } satisfies DropzoneOptions

    const resolveImageUrl = (imageUrl?: string | null) => {
        if (!imageUrl) return ""
        return imageUrl.includes("https://") ? imageUrl : `${VITE_VIEW_S3_URL}/${imageUrl}`
    }

    const resetForm = () => {
        setFiles([])
        reset({ name: "", parent_category_id: 0, image_url: "" })
    }

    const isParentCategory = (categoryId: number) =>
        categories.some((category: any) => category.id === categoryId && !category.parent_category_id)

    const handleTypeChange = (value: string) => {
        setActiveType(value as "facility" | "product" | "member" | "membership" | "credit_add_ons" | "coach")
        setIsAddingCategory(false)
        setEditingCategory(null)
        setAddingSubcategoryTo(null)
        resetForm()
    }

    const toggleCategory = (categoryId: number) => {
        setExpandedCategories((prev) => ({
            ...prev,
            [categoryId]: !prev[categoryId],
        }))
    }

    const onSubmit = async (data: CategoryFormData) => {
        if (!data.name.trim()) return

        try {
            const shouldHandleImage =
                activeType === "facility" &&
                !addingSubcategoryTo &&
                (!editingCategory || isParentCategory(editingCategory))
            const imagePayload: { image_url?: string | null } = {}

            if (shouldHandleImage) {
                imagePayload.image_url = data.image_url || ""
                if (files && files.length > 0) {
                    if (data.image_url) {
                        await deleteCognitoImage(data.image_url)
                    }
                    const getUrl = await UploadCognitoImage(files[0])
                    imagePayload.image_url = getUrl.location
                }
            }

            if (editingCategory) {
                await updateCategory({
                    id: editingCategory,
                    name: data.name.trim(),
                    type: activeType,
                    org_id: orgId,
                    ...imagePayload,
                }).unwrap()
                setEditingCategory(null)
                toast({ title: "Category updated successfully", variant: "success" })
            } else if (addingSubcategoryTo) {
                await createCategory({
                    name: data.name.trim(),
                    type: activeType,
                    parent_category_id: addingSubcategoryTo,
                    org_id: orgId
                }).unwrap()
                setAddingSubcategoryTo(null)
                toast({ title: "Subcategory created successfully", variant: "success" })
            } else {
                await createCategory({
                    name: data.name.trim(),
                    type: activeType,
                    parent_category_id: 0,
                    org_id: orgId,
                    ...imagePayload,
                }).unwrap()
                setIsAddingCategory(false)
                toast({ title: "Category created successfully", variant: "success" })
            }
            resetForm()
        } catch (error) {
            const typedError = error as ErrorType
            toast({
                variant: "destructive",
                title: typedError.data?.detail || typedError.data?.message || "An error occurred"
            })
        }
    }

    const startEdit = (category: any) => {
        setEditingCategory(category.id)
        setFiles([])
        setValue("name", category.name)
        setValue("parent_category_id", category.parent_category_id || 0)
        setValue("image_url", category.image_url || "")
        setAddingSubcategoryTo(null)
        setIsAddingCategory(false)
    }

    const cancelAction = () => {
        setEditingCategory(null)
        setAddingSubcategoryTo(null)
        setIsAddingCategory(false)
        resetForm()
    }

    const renderFacilityImageUploader = () => (
        <FileUploader
            value={files}
            onValueChange={setFiles}
            dropzoneOptions={dropzone}
            className="flex flex-col items-center gap-2 w-[160px] shrink-0"
        >
            {/* Image preview */}
            {files && files.length > 0 ? (
                <FileUploaderContent className="w-full">
                    {files.map((file, i) => (
                        <FileUploaderItem
                            key={i}
                            index={i}
                            className="p-0 rounded-lg overflow-hidden h-[120px] w-full"
                            aria-roledescription={`file ${i + 1} containing ${file.name}`}
                        >
                            <img
                                src={URL.createObjectURL(file)}
                                alt={file.name}
                                className="object-cover h-[120px] w-full rounded-lg"
                            />
                        </FileUploaderItem>
                    ))}
                </FileUploaderContent>
            ) : watcher.image_url ? (
                <div className="h-[120px] w-full rounded-lg overflow-hidden">
                    <img
                        src={resolveImageUrl(watcher.image_url)}
                        loading="lazy"
                        className="object-cover h-full w-full"
                    />
                </div>
            ) : (
                <div className="flex items-center justify-center h-[120px] w-full rounded-lg bg-gray-100 border border-dashed border-gray-300">
                    <i className="text-gray-400 fa-regular fa-image text-2xl"></i>
                </div>
            )}

            {/* Change Image trigger */}
            <FileInput className="w-full">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full gap-1.5 text-xs font-medium"
                >
                    <img src={uploadimg} className="size-4" />
                    {watcher.image_url || (files && files.length > 0) ? "Change Image" : "Upload Image"}
                </Button>
            </FileInput>
        </FileUploader>
    )

    const confirmDelete = async () => {
        if (!categoryToDelete) return
        try {
            await deleteCategory(categoryToDelete).unwrap()
            toast({ title: "Deleted successfully", variant: "success" })
        } catch {
            toast({ title: "Failed to delete", variant: "destructive" })
        }
        setDeleteConfirmOpen(false)
        setCategoryToDelete(null)
    }

    return (
        <div className="p-3 sm:p-5">
            <Card className="w-full max-w-3xl mx-auto">
                <CardHeader className="py-3">
                    <CardTitle className="text-lg font-medium">Categories</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                        <div className="flex items-center gap-3">
                            {/* <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">Category Type:</span> */}
                            <Select value={activeType} onValueChange={handleTypeChange}>
                                <SelectTrigger className="w-full sm:w-[180px]">
                                    <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="facility">Facility</SelectItem>
                                    <SelectItem value="product">Product</SelectItem>
                                    <SelectItem value="member">Members</SelectItem>
                                    <SelectItem value="membership">Membership</SelectItem>
                                    <SelectItem value="credit_add_ons">Credit</SelectItem>
                                    <SelectItem value="coach">Coach</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <Button
                            size="sm"
                            className="bg-primary hover:bg-primary/90"
                            onClick={() => {
                                setIsAddingCategory(true)
                                setEditingCategory(null)
                                setAddingSubcategoryTo(null)
                                resetForm()
                            }}
                        >
                            <Plus className="mr-1 h-4 w-4" /> Add Category
                        </Button>
                    </div>

                    {isAddingCategory && (
                        <form onSubmit={handleSubmit(onSubmit)} className="mb-4 p-4 border rounded-lg bg-muted/20">
                            {activeType === "facility" ? (
                                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                                    {renderFacilityImageUploader()}
                                    <div className="flex flex-col gap-2 flex-1">
                                        <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                            Category Name
                                        </label>
                                        <Input
                                            placeholder="Enter category name"
                                            {...register("name", { required: true })}
                                            className="flex-1"
                                            autoFocus
                                        />
                                        <p className="text-xs text-primary">
                                            Recommended cover 1200×900px · JPG or PNG.
                                        </p>
                                        <div className="flex gap-2 mt-1">
                                            <Button type="submit" size="sm" className="bg-primary hover:bg-primary/90"><Save className="h-4 w-4 mr-1" /> Save</Button>
                                            <Button onClick={cancelAction} size="sm" variant="ghost"><X className="h-4 w-4 mr-1" /> Cancel</Button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <Input placeholder="Category name" {...register("name", { required: true })} className="flex-1" autoFocus />
                                    <Button type="submit" size="sm" className="bg-primary hover:bg-primary/90"><Save className="h-4 w-4" /></Button>
                                    <Button onClick={cancelAction} size="sm" variant="ghost"><X className="h-4 w-4" /></Button>
                                </div>
                            )}
                        </form>
                    )}

                    {isLoading ? (
                        <div className="flex items-center justify-center py-10">
                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                        </div>
                    ) : categories.length === 0 && !isAddingCategory ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <img src={EmptyCategory} alt="No categories" width={160} height={160} className="mb-4 opacity-70" />
                            <h3 className="text-sm font-medium text-muted-foreground">No Categories Found</h3>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {categories.filter(c => !c.parent_category_id).map((category: any) => (
                                <div key={category.id} className="border rounded-md overflow-hidden">
                                    <div className="flex items-center justify-between p-3 bg-muted/10 gap-2">
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                            {/* Enable hierarchy for all generic categories if data exists */}
                                            {activeType === "facility" && category.subcategories && (
                                                <Button variant="ghost" size="sm" className="p-0 h-8 w-8" onClick={() => toggleCategory(category.id)}>
                                                    {expandedCategories[category.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                                </Button>
                                            )}
                                            {activeType !== "facility" && <div className="w-8" />}

                                            {editingCategory === category.id ? (
                                                <form onSubmit={handleSubmit(onSubmit)} className="flex-1 p-2">
                                                    {activeType === "facility" ? (
                                                        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                                                            {renderFacilityImageUploader()}
                                                            <div className="flex flex-col gap-2 flex-1">
                                                                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                                                    Category Name
                                                                </label>
                                                                <Input {...register("name", { required: true })} autoFocus />
                                                                <p className="text-xs text-primary">
                                                                    Recommended cover 1200×900px · JPG or PNG.
                                                                </p>
                                                                <div className="flex gap-2 mt-1">
                                                                    <Button type="submit" size="sm" className="bg-primary h-8"><Save className="h-4 w-4 mr-1" /> Save</Button>
                                                                    <Button onClick={cancelAction} size="sm" variant="ghost" className="h-8"><X className="h-4 w-4 mr-1" /> Cancel</Button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex gap-2">
                                                            <Input {...register("name", { required: true })} className="h-8" autoFocus />
                                                            <Button type="submit" size="sm" className="bg-primary h-8"><Save className="h-4 w-4" /></Button>
                                                            <Button onClick={cancelAction} size="sm" variant="ghost" className="h-8"><X className="h-4 w-4" /></Button>
                                                        </div>
                                                    )}
                                                </form>
                                            ) : (
                                                <>
                                                    {activeType === "facility" && (
                                                        category.image_url ? (
                                                            <img
                                                                src={resolveImageUrl(category.image_url)}
                                                                alt={category.name}
                                                                loading="lazy"
                                                                className="size-12 bg-gray-100 object-cover rounded-lg"
                                                            />
                                                        ) : (
                                                            <div className="size-12 bg-gray-100 rounded-lg flex items-center justify-center">
                                                                <i className="text-gray-400 fa-regular fa-image text-sm"></i>
                                                            </div>
                                                        )
                                                    )}
                                                    <span className="font-medium">{category.name}</span>
                                                </>
                                            )}
                                        </div>

                                       {editingCategory !== category.id && (
                                            <div className="flex items-center gap-1 shrink-0">
                                                {activeType === "facility" && (
                                                    <>
                                                        <Badge variant="outline" className="bg-muted text-xs">
                                                            {category.subcategories?.length || 0}
                                                        </Badge>
                                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-primary" onClick={() => {
                                                            setAddingSubcategoryTo(category.id)
                                                            setExpandedCategories(prev => ({ ...prev, [category.id]: true }))
                                                            setEditingCategory(null)
                                                            setIsAddingCategory(false)
                                                            reset()
                                                        }}>
                                                            <PlusCircle className="h-4 w-4" />
                                                        </Button>
                                                    </>
                                                )}
                                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => startEdit(category)}>

                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive" onClick={() => {
                                                    setCategoryToDelete(category.id)
                                                    setDeleteConfirmOpen(true)
                                                }}>
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Subcategories */}
                                    {activeType === "facility" && expandedCategories[category.id] && (
                                        <div className="bg-muted/5 divide-y">
                                            {addingSubcategoryTo === category.id && (
                                                <form onSubmit={handleSubmit(onSubmit)} className="p-2 pl-10 flex gap-2">
                                                    <Input placeholder="Subcategory name" {...register("name", { required: true })} className="h-8 flex-1" autoFocus />
                                                    <Button type="submit" size="sm" className="bg-primary h-8"><Save className="h-4 w-4" /></Button>
                                                    <Button onClick={cancelAction} size="sm" variant="ghost" className="h-8"><X className="h-4 w-4" /></Button>
                                                </form>
                                            )}
                                            {category.subcategories?.map((sub: any) => (
                                                <div key={sub.id} className="flex items-center justify-between p-2 pl-10">
                                                    {editingCategory === sub.id ? (
                                                        <form onSubmit={handleSubmit(onSubmit)} className="flex-1 flex gap-2">
                                                            <Input {...register("name", { required: true })} className="h-8 flex-1" autoFocus />
                                                            <Button type="submit" size="sm" className="bg-primary h-8"><Save className="h-4 w-4" /></Button>
                                                            <Button onClick={cancelAction} size="sm" variant="ghost" className="h-8"><X className="h-4 w-4" /></Button>
                                                        </form>
                                                    ) : (
                                                        <>
                                                            <span className="text-sm">{sub.name}</span>
                                                            <div className="flex items-center gap-1">
                                                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => startEdit(sub)}>
                                                                    <Edit className="h-3.5 w-3.5" />
                                                                </Button>
                                                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => {
                                                                    setCategoryToDelete(sub.id)
                                                                    setDeleteConfirmOpen(true)
                                                                }}>
                                                                    <Trash2 className="h-3.5 w-3.5" />
                                                                </Button>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            <ConfirmationDialog
                isOpen={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                onConfirm={confirmDelete}
                title="Delete Category"
                message="Are you sure you want to delete this category? This action cannot be undone."
                confirmTitle="Delete"
            />
        </div>
    )
}
